import React, { useEffect, useState } from 'react';
import {
  Grid, Paper, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Typography, makeStyles, CircularProgress,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { analyticsService } from '../../services';
import { fmtNum } from '../../components/analytics/format';

const useStyles = makeStyles(() => ({
  form: { padding: 18, marginBottom: 16 },
  title: { fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 12 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' },
  field: { minWidth: 180 },
  error: { color: '#d32f2f', marginTop: 8, fontSize: 13 },
  tableWrap: { padding: 18 },
}));

type Account = { _id: string; mobile?: string; email?: string; label?: string; createdOn?: string };

const TestAccountsTab: React.FC = () => {
  const classes = useStyles();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await analyticsService.listTestAccounts();
    if (res.ok) setAccounts(res.data?.accounts || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    setError('');
    if (!mobile.trim() && !email.trim()) {
      setError('Enter a mobile number or email.');
      return;
    }
    setSaving(true);
    const res = await analyticsService.addTestAccount({
      mobile: mobile.trim() || undefined,
      email: email.trim() || undefined,
      label: label.trim() || undefined,
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || 'Failed to add test account.');
      return;
    }
    setMobile(''); setEmail(''); setLabel('');
    load();
  };

  const handleDelete = async (id: string) => {
    const res = await analyticsService.deleteTestAccount(id);
    if (res.ok) setAccounts((prev) => prev.filter((a) => a._id !== id));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper className={classes.form} elevation={1}>
          <Typography className={classes.title}>Add a test account</Typography>
          <div className={classes.row}>
            <TextField
              className={classes.field}
              label="Mobile number"
              variant="outlined"
              size="small"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
            <TextField
              className={classes.field}
              label="Email"
              variant="outlined"
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              className={classes.field}
              label="Label (optional)"
              variant="outlined"
              size="small"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Button variant="contained" color="primary" onClick={handleAdd} disabled={saving}>
              {saving ? 'Adding…' : 'Add'}
            </Button>
          </div>
          {error && <div className={classes.error}>{error}</div>}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.tableWrap} elevation={1}>
          <Typography className={classes.title}>Test accounts ({fmtNum(accounts.length)})</Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((a) => (
                  <TableRow key={a._id}>
                    <TableCell>{a.mobile || '—'}</TableCell>
                    <TableCell>{a.email || '—'}</TableCell>
                    <TableCell>{a.label || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDelete(a._id)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {accounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary">No test accounts yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TestAccountsTab;
